import { useQuery } from '@apollo/client'
import * as Sentry from '@sentry/browser'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { Channel as ChannelInstance, ConnectionOpen, StreamChat, UserResponse } from 'stream-chat'

import { GET_WORKSPACE_MEMBERS } from '../data/queries/members'
import { useAuth } from '../hooks/use-auth'
import { useChatClient } from '../hooks/use-chat-client'
import { useUserQuery } from '../hooks/use-user-query'
import { useWorkspaceUsers } from '../hooks/use-workspace-users'
import type { IChatUser } from '../types/chat-types'
import type { IWorkspaceMember } from '../types/user'
import { MembershipStatus } from '../types/user'
import { TOKEN_UPDATED_EVENT, tokenUtils } from '../utils/token-utils'
import { useChatConnection } from './__hooks/use-chat-connection'
import { useChatUserSearch } from './__hooks/use-chat-user-search'
import { createGenericContext } from './generic-context'

type TConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error'
type QueryChannelsArgs = Parameters<StreamChat['queryChannels']>
type QueryChannelsResult = ReturnType<StreamChat['queryChannels']>

const QUERY_CHANNELS_504_BASE_DELAY_MS = 300
const MAX_QUERY_CHANNELS_504_RETRIES = 3

interface IErrorWithStatusCode {
  code?: number | string
  message?: string
  response?: { status?: number }
  status?: number
}

const sleep = async (ms: number) => await new Promise((resolve) => setTimeout(resolve, ms))

const isValid504Error = (error: unknown): boolean => {
  const errorObject = (error as IErrorWithStatusCode | undefined) || {}
  const message = errorObject?.message || ''
  const code = errorObject?.code
  const status = errorObject?.status || errorObject?.response?.status

  return message.includes('HTTP code: 504') || code === 504 || code === '504' || status === 504
}

interface IChatContext {
  activeChatID: string | null
  chatClient: StreamChat
  connectionStatus: TConnectionStatus
  disconnectUser: () => void
  isLoading: boolean
  mentionableUsers: UserResponse[]
  searchUser: (inputValue: string) => Promise<IChatUser[] | []>
  teamMembers: IWorkspaceMember[]
  onSetActiveChat: (channel: ChannelInstance | null) => void
  unreadCount: number
  userConnection: ConnectionOpen | null
  workspaceUsers: (UserResponse & IChatUser)[]
}

const [useChat, ChatContextProvider] = createGenericContext<IChatContext>(
  'useChat must be used within the ChatContextProvider'
)

interface ChatProviderProps {
  children: React.ReactNode
}

/**
 * ChatProvider wires together the Stream Chat client, connection lifecycle,
 * workspace user list, and user search into a single context consumed by all
 * chat-aware components in the tree.
 *
 * Responsibilities:
 * - Bootstraps the StreamChat client via `useChatClient`
 * - Delegates connection management (connect / disconnect / reconnect) to `useChatConnection`
 * - Resolves mentionable workspace users via `useWorkspaceUsers`
 * - Provides a `searchUser` helper for @-mention search via `useChatUserSearch`
 * - Attaches and cleans up global client event listeners on mount/unmount
 */
const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { selectedWorkspace, isWorkspacesLoading } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const { myDetails: userDetails } = useUserQuery()
  const {
    client: chatClientService,
    addListeners,
    removeListeners,
    activeChatID,
    setActiveChat: onSetActiveChat
  } = useChatClient()
  const [userChatToken, setUserChatToken] = useState<string | null>(() => tokenUtils.getToken('User-Chat-Token'))
  const originalQueryChannelsRef = useRef<StreamChat['queryChannels'] | null>(null)
  const userIDRef = useRef<string | undefined>(userDetails?.id)
  userIDRef.current = userDetails?.id

  const { connectionStatus, unreadCount, userConnection, disconnectUser } = useChatConnection(
    chatClientService,
    userDetails,
    userChatToken
  )

  const { searchUser } = useChatUserSearch(chatClientService, userDetails)

  // Fetched lazily by `useWorkspaceUsers` — skipped here to avoid an immediate network call
  const { refetch: fetchWorkspaceMembers } = useQuery<{
    workspaceMembers: IWorkspaceMember[]
  }>(GET_WORKSPACE_MEMBERS, {
    context: { headers: { 'ws-id': workspaceID } },
    skip: true,
    variables: { input: { membershipStatuses: [MembershipStatus.ACTIVE] } }
  })

  const { mentionableUsers, teamMembers, isLoading } = useWorkspaceUsers(
    userDetails,
    chatClientService,
    workspaceID,
    fetchWorkspaceMembers,
    isWorkspacesLoading
  )

  // Attach global Stream Chat client listeners once on mount (message.new, notifications,
  // storage sync, beforeunload). Cleaned up on unmount to prevent memory leaks.
  useEffect(() => {
    addListeners()
    return () => removeListeners()
  }, [])

  // Keep the chat token reactive in this session so Stream connection can recover
  // when auth flows set/remove the User-Chat-Token after initial render.
  useEffect(() => {
    const syncChatToken = () => {
      setUserChatToken(tokenUtils.getToken('User-Chat-Token'))
    }

    syncChatToken()

    const handleTokenUpdated = (event: Event) => {
      const tokenEvent = event as CustomEvent<{ tokenType?: string }>
      if (!tokenEvent.detail?.tokenType || tokenEvent.detail.tokenType === 'User-Chat-Token') {
        syncChatToken()
      }
    }

    window.addEventListener(TOKEN_UPDATED_EVENT, handleTokenUpdated as EventListener)
    return () => {
      window.removeEventListener(TOKEN_UPDATED_EVENT, handleTokenUpdated as EventListener)
    }
  }, [])

  useEffect(() => {
    if (originalQueryChannelsRef.current) return

    const originalQueryChannels = chatClientService.queryChannels.bind(chatClientService)
    originalQueryChannelsRef.current = originalQueryChannels as StreamChat['queryChannels']

    const wrappedQueryChannels = (async (...args: QueryChannelsArgs): QueryChannelsResult => {
      let retryAttempt = 0

      while (true) {
        try {
          return (await originalQueryChannels(...args)) as unknown as QueryChannelsResult
        } catch (error) {
          if (!isValid504Error(error) || retryAttempt >= MAX_QUERY_CHANNELS_504_RETRIES) {
            if (isValid504Error(error) && retryAttempt >= MAX_QUERY_CHANNELS_504_RETRIES) {
              Sentry.captureMessage('Stream queryChannels HTTP 504 retry exhausted', {
                extra: {
                  maxRetries: MAX_QUERY_CHANNELS_504_RETRIES,
                  userID: userIDRef.current || 'unknown'
                },
                level: 'error',
                tags: {
                  component: 'ChatProvider',
                  operation: 'queryChannels'
                }
              })
            }
            throw error
          }

          retryAttempt += 1
          const retryDelayMs = QUERY_CHANNELS_504_BASE_DELAY_MS * Math.pow(2, retryAttempt - 1)

          Sentry.addBreadcrumb({
            category: 'chat',
            data: {
              maxRetries: MAX_QUERY_CHANNELS_504_RETRIES,
              retryAttempt,
              retryDelayMs,
              userID: userIDRef.current || 'unknown'
            },
            level: 'warning',
            message: 'Stream queryChannels received HTTP 504, retrying'
          })

          await sleep(retryDelayMs)
        }
      }
    }) as StreamChat['queryChannels']

    chatClientService.queryChannels = wrappedQueryChannels

    return () => {
      if (!originalQueryChannelsRef.current) return
      chatClientService.queryChannels = originalQueryChannelsRef.current
      originalQueryChannelsRef.current = null
    }
  }, [chatClientService])

  const contextValue: IChatContext = useMemo(
    () => ({
      chatClient: chatClientService,
      activeChatID,
      onSetActiveChat,
      connectionStatus,
      disconnectUser,
      isLoading,
      mentionableUsers,
      searchUser,
      teamMembers,
      unreadCount,
      userConnection,
      workspaceUsers: []
    }),
    [
      activeChatID,
      chatClientService,
      connectionStatus,
      disconnectUser,
      isLoading,
      mentionableUsers,
      onSetActiveChat,
      searchUser,
      teamMembers,
      unreadCount,
      userConnection
    ]
  )

  return <ChatContextProvider value={contextValue}>{children}</ChatContextProvider>
}

export { ChatContextProvider, ChatProvider, useChat }
